import { Link } from 'react-router-dom';

const stats = [
  {
    icon: 'ri-money-dollar-circle-line',
    value: '$3K–$8K',
    label: 'Average savings vs. dealerships',
    note: 'Real numbers. Not marketing math.',
  },
  {
    icon: 'ri-price-tag-3-line',
    value: '$995',
    label: 'Flat broker fee. Period.',
    note: 'No percentage markups. No mystery charges.',
  },
  {
    icon: 'ri-file-list-3-line',
    value: '$0',
    label: 'Hidden fees',
    note: 'Doc fees, dealer prep, market adjustments — none of that.',
  },
  {
    icon: 'ri-thumb-up-line',
    value: '100%',
    label: 'Transparent pricing',
    note: 'The price you see is the price you pay.',
  },
];

const callouts = [
  {
    icon: 'ri-close-circle-line',
    them: '"Let me check with my manager."',
    us: 'Our team can actually make decisions. Wild, right?',
  },
  {
    icon: 'ri-close-circle-line',
    them: 'One brand. One lot. Take it or leave it.',
    us: 'Any make, any model, any state. We go find it.',
  },
  {
    icon: 'ri-close-circle-line',
    them: 'Rate markup baked into your loan.',
    us: 'We shop lenders. You get the best rate, not the most profitable one.',
  },
];

export default function WhyRockhoundStrip() {
  return (
    <section className="py-20 bg-[#1A2332] relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E8B84B] rounded-full -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#E8B84B] rounded-full translate-y-1/2 -translate-x-1/3"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-[#E8B84B]/15 border border-[#E8B84B]/40 rounded-full px-4 py-2 mb-5">
            <i className="ri-award-line text-[#E8B84B]"></i>
            <span className="text-sm font-semibold text-[#E8B84B]">Why Rockhound</span>
          </div>
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-4">
            We're Not Like Other Dealers.
            <br />
            <span className="text-[#E8B84B]">That's Literally the Point.</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            The dealership model is built to extract money from you at every step. Ours isn't. Here's what that actually looks like in practice.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((stat) => (
            <div
              key={stat.value}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 hover:border-[#E8B84B]/30 transition-all"
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3" aria-hidden="true">
                <i className={`${stat.icon} text-[#E8B84B] text-2xl`}></i>
              </div>
              <div className="font-['Oswald'] text-3xl font-bold text-[#E8B84B] mb-1">{stat.value}</div>
              <div className="text-sm font-semibold text-white mb-1">{stat.label}</div>
              <div className="text-xs text-gray-400">{stat.note}</div>
            </div>
          ))}
        </div>

        {/* Them vs Us Callouts */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-10">
          <h3 className="font-['Oswald'] text-2xl font-bold text-white mb-6 text-center">
            Sound Familiar? Here's the Difference.
          </h3>
          <ul className="space-y-5 list-none p-0" aria-label="Dealership vs Rockhound comparison">
            {callouts.map((item, i) => (
              <li key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-4">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                    <i className="ri-close-circle-fill text-red-400 text-lg"></i>
                  </div>
                  <p className="text-sm text-gray-300 italic">{item.them}</p>
                </div>
                <div className="flex items-start gap-3 bg-[#E8B84B]/10 border border-[#E8B84B]/25 rounded-xl px-5 py-4">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                    <i className="ri-checkbox-circle-fill text-[#E8B84B] text-lg"></i>
                  </div>
                  <p className="text-sm text-white font-medium">{item.us}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-gray-400 text-base mb-5">
            Still not convinced? Read what our customers say — or just call us and ask the hard questions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/compare"
              className="inline-flex items-center gap-2 bg-[#E8B84B] hover:bg-[#d4a43a] text-[#1A2332] font-bold px-7 py-3 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-scales-3-line"></i>
              See the Full Comparison
            </Link>
            <Link
              to="/reviews"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-7 py-3 rounded-lg border border-white/20 transition-colors whitespace-nowrap"
            >
              <i className="ri-star-line"></i>
              Read Customer Reviews
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
